import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSessionState } from '../hooks/useSessionState';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Search, 
  Users, 
  Plus, 
  Edit, 
  Upload, 
  Loader2,
  UserCheck,
  UserX,
  FileSpreadsheet,
  Mail,
  Trash2,
  AlertTriangle,
  LogIn,
  KeyRound,
  Shield,
  RotateCcw,
  Star,
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEPARTMENTS = [
  "Administration",
  "Administration (Travel)",
  "Business Development",
  "Design & Development",
  "Design & Engineering",
  "Design and Technical",
  "Digital Transformation",
  "Finance and Accounts",
  "Finance and Accounts (CCD)",
  "Human Resources",
  "Human Resources Management",
  "Information Technology",
  "International Business",
  "Learning & Development",
  "Legal",
  "Logistics & Dispatch",
  "Logistics and Dispatch (EXIM)",
  "Maintenance and Engineering",
  "Management",
  "Management and Strategy",
  "Marketing and Communications",
  "Operations",
  "Procurement",
  "Production",
  "Production Planning",
  "Projects",
  "Quality Management",
  "Quality and Technical Support",
  "Safety",
  "Sales",
  "Sales Administration & Support Operations",
  "Sales and Business Development",
  "Stores (Warehouse)",
  "Technical and New Product Development (R&D)"
];

const ROLES = ["Employee", "Admin Team", "Admin Head", "IT Team", "IT Head", "Super Admin", "HR", "Management"];

export default function EmployeesPage() {
  const { getAuthHeader, isAdmin, employee: currentEmployee, impersonateUser, adminResetPassword } = useAuth();
  const isSuperAdmin = currentEmployee?.role === 'Super Admin';
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useSessionState('employees:search', '');
  const [showInactive, setShowInactive] = useSessionState('employees:showInactive', false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Dialog states
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [resetPwDialog, setResetPwDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('12345678');
  const [actionLoading, setActionLoading] = useState(false);

  // Permissions state
  const [permDialog, setPermDialog] = useState(null); // employee or null
  const [allModules, setAllModules] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState({});
  const [permMatrix, setPermMatrix] = useState({});
  const [permLoading, setPermLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    employee_code: '',
    full_name: '',
    email: '',
    line_manager_name: '',
    line_manager_email: '',
    approval_delegate_email: '',
    department: '',
    role: 'Employee',
    entity: '',
    grade: '',
    location: '',
    sum_insured: '',
    is_active: true,
    is_spoc: false
  });

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/employees?include_inactive=${showInactive}`, {
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        const data = await res.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Fetch employees error:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, showInactive]);

  useEffect(() => {
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin, fetchEmployees]);

  // Load module list once for Super Admin
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const res = await fetch(`${API}/permissions/modules`, { headers: getAuthHeader() });
        if (res.ok) {
          const data = await res.json();
          setAllModules(data.modules || []);
          setRoleTemplates(data.role_templates || {});
        }
      } catch (e) { /* ignore */ }
    })();
  }, [isSuperAdmin, getAuthHeader]);

  const openPermissionsDialog = async (emp) => {
    setPermDialog(emp);
    setPermLoading(true);
    try {
      const res = await fetch(`${API}/permissions/${emp.employee_id}`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        setPermMatrix(data.permissions || {});
      }
    } catch (e) {
      toast.error('Failed to load permissions');
    } finally {
      setPermLoading(false);
    }
  };

  const togglePerm = (moduleKey, action) => {
    setPermMatrix(prev => ({
      ...prev,
      [moduleKey]: {
        ...(prev[moduleKey] || { view: false, create: false, edit: false, approve: false }),
        [action]: !prev?.[moduleKey]?.[action],
      },
    }));
  };

  const toggleModuleAll = (moduleKey, value) => {
    setPermMatrix(prev => ({
      ...prev,
      [moduleKey]: { view: value, create: value, edit: value, approve: value },
    }));
  };

  const savePermissions = async () => {
    if (!permDialog) return;
    setPermLoading(true);
    try {
      const res = await fetch(`${API}/permissions/${permDialog.employee_id}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ module_permissions: permMatrix }),
      });
      if (res.ok) {
        toast.success('Permissions updated');
        setPermDialog(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update permissions');
      }
    } catch (e) {
      toast.error('Failed to update permissions');
    } finally {
      setPermLoading(false);
    }
  };

  const resetToRoleTemplate = async () => {
    if (!permDialog) return;    if (!permDialog) return;
    setPermLoading(true);
    try {
      const res = await fetch(`${API}/permissions/${permDialog.employee_id}/apply-template`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      if (res.ok) {
        const data = await res.json();
        setPermMatrix(data.permissions || {});
        toast.success('Reset to role template');
      }
    } catch (e) {
      toast.error('Failed to reset');
    } finally {
      setPermLoading(false);
    }
  };

  const handleInlineRoleChange = async (employee, newRole) => {
    try {
      const res = await fetch(`${API}/employees/${employee.employee_id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify({
          ...employee,
          role: newRole
        })
      });

      if (res.ok) {
        toast.success(`Role updated to ${newRole}`);
        fetchEmployees();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const [resendingEmail, setResendingEmail] = useState(null);

  const handleClearUnrated = async (emp) => {
    if (!window.confirm(`Mark all of ${emp.full_name}'s unrated completed tickets as skipped? This will unblock them from raising new requests.`)) return;
    try {
      const res = await fetch(`${API}/tickets/admin-clear-unrated/${encodeURIComponent(emp.email)}`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Cleared ${data.modified} pending rating(s) for ${emp.full_name}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to clear pending ratings');
      }
    } catch (e) {
      toast.error('Failed to clear pending ratings');
    }
  };

  const handleResendSetupEmail = async (employee) => {
    setResendingEmail(employee.employee_id);
    try {
      const res = await fetch(`${API}/auth/resend-setup-email?email=${encodeURIComponent(employee.email)}`, {
        method: 'POST',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        const data = await res.json();
        if (data.email_sent) {
          toast.success(`Setup email sent to ${employee.email}`);
        } else {
          toast.warning('Token generated but email could not be sent');
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send setup email');
      }
    } catch (error) {
      toast.error('Failed to send setup email');
    } finally {
      setResendingEmail(null);
    }
  };

  const filteredEmployees = employees.filter(emp => 
    emp.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.employee_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.department.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      employee_code: '',
      full_name: '',
      email: '',
      line_manager_name: '',
      line_manager_email: '',
      approval_delegate_email: '',
      department: '',
      role: 'Employee',
      entity: '',
      grade: '',
      location: '',
      sum_insured: '',
      is_active: true,
      is_spoc: false
    });
  };

  const handleAddEmployee = async () => {
    if (!formData.employee_code || !formData.full_name || !formData.email || !formData.department || !formData.line_manager_email || !formData.entity) {
      toast.error('Please fill all required fields');
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${API}/employees`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Employee added successfully');
        setAddDialog(false);
        resetForm();
        fetchEmployees();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add employee');
      }
    } catch (error) {
      toast.error('Failed to add employee');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditEmployee = async () => {
    if (!selectedEmployee) return;

    setActionLoading(true);
    try {
      const res = await fetch(`${API}/employees/${selectedEmployee.employee_id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Employee updated successfully');
        setEditDialog(false);
        setSelectedEmployee(null);
        resetForm();
        fetchEmployees();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update employee');
      }
    } catch (error) {
      toast.error('Failed to update employee');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await fetch(`${API}/employees/upload`, {
        method: 'POST',
        headers: getAuthHeader(),
        
        body: formDataUpload
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.errors?.length > 0) {
          data.errors.forEach(err => toast.error(err));
        }
        fetchEmployees();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = () => {
    window.open(`${API}/employees/download/template`, '_blank');
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;
    
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/employees/${selectedEmployee.employee_id}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
        
      });

      if (res.ok) {
        toast.success('Employee deleted successfully');
        setDeleteDialog(false);
        setSelectedEmployee(null);
        fetchEmployees();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete employee');
      }
    } catch (error) {
      toast.error('Failed to delete employee');
    } finally {
      setActionLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Access denied. Admin privileges required.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="employees-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Employee Details</h1>
            <p className="text-muted-foreground mt-1">
              Manage employee records and roles
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={downloadTemplate}
              className="gap-2"
              data-testid="download-template-btn"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Template
            </Button>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="gap-2"
              data-testid="upload-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Import
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleFileUpload}
            />
            <Button
              onClick={() => {
                resetForm();
                setAddDialog(true);
              }}
              className="gap-2"
              data-testid="add-employee-btn"
            >
              <Plus className="w-4 h-4" />
              Add Employee
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-employees-input"
            />
          </div>
          <Button
            variant={showInactive ? "default" : "outline"}
            onClick={() => setShowInactive(!showInactive)}
            className="gap-2"
            data-testid="show-inactive-btn"
          >
            {showInactive ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
            {showInactive ? 'Showing All' : 'Active Only'}
          </Button>
        </div>

        {/* Employees Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Employees
            </CardTitle>
            <CardDescription>
              {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground empty-state">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No employees found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="hidden md:table-cell">Email</TableHead>
                      <TableHead className="hidden lg:table-cell">Department</TableHead>
                      <TableHead className="hidden sm:table-cell">Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEmployees.map((emp) => (
                      <TableRow key={emp.employee_id} className="table-row-hover">
                        <TableCell className="font-mono text-sm">
                          {emp.employee_code}
                        </TableCell>
                        <TableCell className="font-medium">
                          {emp.full_name}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground">
                          {emp.email}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground">
                          {emp.department}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <Select
                            value={emp.role}
                            onValueChange={(newRole) => handleInlineRoleChange(emp, newRole)}
                          >
                            <SelectTrigger className="h-8 w-[130px]" data-testid={`role-select-${emp.employee_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ROLES.map((role) => (
                                <SelectItem key={role} value={role}>
                                  {role}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Badge variant={emp.is_active ? 'default' : 'destructive'} className={emp.is_active ? 'bg-green-600' : ''}>
                            {emp.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleResendSetupEmail(emp)}
                              disabled={resendingEmail === emp.employee_id}
                              title="Resend setup email"
                              data-testid={`resend-email-${emp.employee_id}`}
                            >
                              {resendingEmail === emp.employee_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Mail className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedEmployee(emp);
                                setFormData({
                                  employee_code: emp.employee_code,
                                  full_name: emp.full_name,
                                  email: emp.email,
                                  line_manager_name: emp.line_manager_name || '',
                                  line_manager_email: emp.line_manager_email || '',
                                  approval_delegate_email: emp.approval_delegate_email || '',
                                  department: emp.department,
                                  role: emp.role,
                                  entity: emp.entity || '',
                                  grade: emp.grade || '',
                                  location: emp.location || '',
                                  sum_insured: emp.sum_insured || '',
                                  is_active: emp.is_active,
                                  is_spoc: !!emp.is_spoc
                                });
                                setEditDialog(true);
                              }}
                              data-testid={`edit-${emp.employee_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => {
                                setSelectedEmployee(emp);
                                setDeleteDialog(true);
                              }}
                              title="Delete employee"
                              data-testid={`delete-${emp.employee_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            {isSuperAdmin && emp.email !== currentEmployee?.email && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={async () => {
                                    try {
                                      await impersonateUser(emp.email);
                                      toast.success(`Now viewing as ${emp.full_name}`);
                                      window.location.href = '/dashboard';
                                    } catch (err) {
                                      toast.error(err.message);
                                    }
                                  }}
                                  title="Login as this user"
                                  data-testid={`impersonate-${emp.employee_id}`}
                                >
                                  <LogIn className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  onClick={() => { setResetPwDialog(emp); setNewPassword('12345678'); }}
                                  title="Reset password"
                                  data-testid={`reset-pw-${emp.employee_id}`}
                                >
                                  <KeyRound className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => openPermissionsDialog(emp)}
                                  title="Access & Permissions"
                                  data-testid={`permissions-${emp.employee_id}`}
                                >
                                  <Shield className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                                  onClick={() => handleClearUnrated(emp)}
                                  title="Clear pending ratings (unblock for new request)"
                                  data-testid={`clear-unrated-${emp.employee_id}`}
                                >
                                  <Star className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Employee Dialog */}
        <Dialog open={addDialog} onOpenChange={setAddDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Employee</DialogTitle>
              <DialogDescription>
                Enter the employee details below.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee Code *</Label>
                  <Input
                    value={formData.employee_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, employee_code: e.target.value }))}
                    placeholder="EMP-001"
                    data-testid="employee-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="John Doe"
                    data-testid="full-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john.d@united-group.in"
                  data-testid="email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Line Manager Name</Label>
                <Input
                  value={formData.line_manager_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, line_manager_name: e.target.value }))}
                  placeholder="Jane Manager"
                  data-testid="manager-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Line Manager Email *</Label>
                <Input
                  type="email"
                  value={formData.line_manager_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, line_manager_email: e.target.value }))}
                  placeholder="jane.m@united-group.in"
                  data-testid="manager-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Approval Delegate Email (Optional)</Label>
                <Input
                  type="email"
                  value={formData.approval_delegate_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, approval_delegate_email: e.target.value }))}
                  placeholder="delegate@united-group.in"
                  data-testid="delegate-email-input"
                />
                <p className="text-xs text-muted-foreground">
                  If set, approval requests will be routed to this email instead of the line manager.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Department *</Label>
                <Select 
                  value={formData.department} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, department: value }))}
                >
                  <SelectTrigger data-testid="department-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger data-testid="role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Entity *</Label>
                <Select 
                  value={formData.entity} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, entity: value }))}
                >
                  <SelectTrigger data-testid="entity-select">
                    <SelectValue placeholder="Select entity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UOSPL">UOSPL</SelectItem>
                    <SelectItem value="UAFPL">UAFPL</SelectItem>
                    <SelectItem value="UAPL">UAPL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Grade</Label>
                  <Input
                    value={formData.grade}
                    onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                    placeholder="e.g. Grade 8"
                    data-testid="grade-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g. Mumbai"
                    data-testid="location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sum Insured</Label>
                  <Input
                    type="number"
                    value={formData.sum_insured}
                    onChange={(e) => setFormData(prev => ({ ...prev, sum_insured: e.target.value }))}
                    placeholder="e.g. 400000"
                    data-testid="sum-insured-input"
                  />
                </div>
                {isSuperAdmin && (
                  <div className="space-y-2">
                    <Label>SPOC</Label>
                    <Select
                      value={formData.is_spoc ? 'yes' : 'no'}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, is_spoc: value === 'yes' }))}
                    >
                      <SelectTrigger data-testid="add-spoc-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddEmployee} 
                disabled={actionLoading}
                data-testid="confirm-add-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add Employee
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Employee Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Employee</DialogTitle>
              <DialogDescription>
                Update the employee details below.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee Code *</Label>
                  <Input
                    value={formData.employee_code}
                    onChange={(e) => setFormData(prev => ({ ...prev, employee_code: e.target.value }))}
                    data-testid="edit-employee-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                    data-testid="edit-full-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="edit-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Line Manager Name</Label>
                <Input
                  value={formData.line_manager_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, line_manager_name: e.target.value }))}
                  data-testid="edit-manager-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Line Manager Email</Label>
                <Input
                  type="email"
                  value={formData.line_manager_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, line_manager_email: e.target.value }))}
                  data-testid="edit-manager-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Approval Delegate Email (Optional)</Label>
                <Input
                  type="email"
                  value={formData.approval_delegate_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, approval_delegate_email: e.target.value }))}
                  placeholder="delegate@united-group.in"
                  data-testid="edit-delegate-email-input"
                />
                <p className="text-xs text-muted-foreground">
                  If set, approval requests will be routed to this email.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Department *</Label>
                <Select 
                  value={formData.department} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, department: value }))}
                >
                  <SelectTrigger data-testid="edit-department-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
                >
                  <SelectTrigger data-testid="edit-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Entity</Label>
                <Select 
                  value={formData.entity} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, entity: value }))}
                >
                  <SelectTrigger data-testid="edit-entity-select">
                    <SelectValue placeholder="Select entity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UOSPL">UOSPL</SelectItem>
                    <SelectItem value="UAFPL">UAFPL</SelectItem>
                    <SelectItem value="UAPL">UAPL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Grade</Label>
                  <Input
                    value={formData.grade}
                    onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                    placeholder="e.g. Grade 8"
                    data-testid="edit-grade-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g. Mumbai"
                    data-testid="edit-location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sum Insured</Label>
                  <Input
                    type="number"
                    value={formData.sum_insured}
                    onChange={(e) => setFormData(prev => ({ ...prev, sum_insured: e.target.value }))}
                    placeholder="e.g. 400000"
                    data-testid="edit-sum-insured-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={formData.is_active ? 'active' : 'inactive'} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, is_active: value === 'active' }))}
                >
                  <SelectTrigger data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isSuperAdmin && (
                <div className="space-y-2">
                  <Label>SPOC</Label>
                  <Select
                    value={formData.is_spoc ? 'yes' : 'no'}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, is_spoc: value === 'yes' }))}
                  >
                    <SelectTrigger data-testid="edit-spoc-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">If Yes, this employee becomes a SPOC for their department ({formData.department || '—'}) and is informed on Guest Arrival / Client Factory Visit requests for it.</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Cancel
              </Button>
              {isSuperAdmin && selectedEmployee && selectedEmployee.email !== currentEmployee?.email && (
                <Button
                  variant="outline"
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                  onClick={() => {
                    setEditDialog(false);
                    openPermissionsDialog(selectedEmployee);
                  }}
                  data-testid="open-permissions-from-edit-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Access & Permissions
                </Button>
              )}
              <Button 
                onClick={handleEditEmployee} 
                disabled={actionLoading}
                data-testid="confirm-edit-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                Delete Employee
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to permanently delete this employee? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {selectedEmployee && (
              <div className="py-4 space-y-2">
                <div className="p-4 bg-muted rounded-lg space-y-1">
                  <p className="font-medium">{selectedEmployee.full_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedEmployee.email}</p>
                  <p className="text-sm text-muted-foreground">{selectedEmployee.department}</p>
                </div>
                <p className="text-sm text-destructive">
                  This will permanently remove the employee from the database along with their user account.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialog(false);
                  setSelectedEmployee(null);
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteEmployee}
                disabled={actionLoading}
                data-testid="confirm-delete-btn"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Delete Employee
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog - Super Admin only */}
        <Dialog open={!!resetPwDialog} onOpenChange={() => setResetPwDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Set a new password for {resetPwDialog?.full_name} ({resetPwDialog?.email})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label>New Password</Label>
                <Input value={newPassword} onChange={e => setNewPassword(e.target.value)} type="text" placeholder="Enter new password" data-testid="reset-pw-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetPwDialog(null)}>Cancel</Button>
              <Button onClick={async () => {
                try {
                  await adminResetPassword(resetPwDialog.email, newPassword);
                  toast.success(`Password reset for ${resetPwDialog.full_name}`);
                  setResetPwDialog(null);
                } catch (err) { toast.error(err.message); }
              }} data-testid="confirm-reset-pw-btn">Reset Password</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Access & Permissions Dialog - Super Admin only */}
        <Dialog open={!!permDialog} onOpenChange={() => setPermDialog(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-600" />
                Access & Permissions
              </DialogTitle>
              <DialogDescription>
                {permDialog?.full_name} ({permDialog?.email}) — Role: <span className="font-medium">{permDialog?.role}</span>
              </DialogDescription>
            </DialogHeader>
            {permDialog?.role === 'Super Admin' ? (
              <div className="p-6 text-sm text-muted-foreground bg-amber-50 dark:bg-amber-950/20 rounded-md border border-amber-200">
                Super Admin has full access to all modules. Permissions cannot be restricted.
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                {permLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50 sticky top-0">
                          <TableHead className="w-[40%]">Module</TableHead>
                          <TableHead className="text-center">View</TableHead>
                          <TableHead className="text-center">Create</TableHead>
                          <TableHead className="text-center">Edit</TableHead>
                          <TableHead className="text-center">Approve</TableHead>
                          <TableHead className="text-center w-24">All</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allModules.map(mod => {
                          const row = permMatrix[mod.key] || { view: false, create: false, edit: false, approve: false };
                          const allOn = row.view && row.create && row.edit && row.approve;
                          return (
                            <TableRow key={mod.key} data-testid={`perm-row-${mod.key}`}>
                              <TableCell className="font-medium">{mod.label}</TableCell>
                              {['view','create','edit','approve'].map(action => (
                                <TableCell key={action} className="text-center">
                                  <Checkbox
                                    checked={!!row[action]}
                                    onCheckedChange={() => togglePerm(mod.key, action)}
                                    data-testid={`perm-${mod.key}-${action}`}
                                  />
                                </TableCell>
                              ))}
                              <TableCell className="text-center">
                                <Checkbox
                                  checked={allOn}
                                  onCheckedChange={(v) => toggleModuleAll(mod.key, !!v)}
                                  data-testid={`perm-${mod.key}-all`}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="flex items-center justify-between gap-2 pt-3 border-t">
              <Button
                variant="outline"
                onClick={resetToRoleTemplate}
                disabled={permLoading || permDialog?.role === 'Super Admin'}
                data-testid="perm-reset-template-btn"
                className="mr-auto"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to {permDialog?.role} defaults
              </Button>
              <Button variant="outline" onClick={() => setPermDialog(null)}>Cancel</Button>
              <Button
                onClick={savePermissions}
                disabled={permLoading || permDialog?.role === 'Super Admin'}
                data-testid="perm-save-btn"
              >
                {permLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Permissions
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
